import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
} from '@mui/material'
import { Users, Trash2, Plus, Edit, Key, AlertCircle, Moon, Sun } from 'lucide-react'
import { settingsAPI } from '../services/api'
import { toast } from 'react-hot-toast'
import { useAuth } from '../hooks/useAuth'
import { useTheme } from '../context/ThemeContext'
import { availableThemes } from '../theme'
import NotificationsTab from '../components/NotificationsTab'
import PreferencesTab from '../components/PreferencesTab'
import PackagesTab from '../components/PackagesTab'
import ExportImportTab from '../components/ExportImportTab'
import LogManagementTab from '../components/LogManagementTab'
import CacheManagementTab from '../components/CacheManagementTab'
import MountsManagementTab from '../components/MountsManagementTab'
import SystemSettingsTab from '../components/SystemSettingsTab'
import BetaFeaturesTab from '../components/BetaFeaturesTab'
import MqttSettingsTab from '../components/MqttSettingsTab'
import Scripts from './Scripts'
import Activity from './Activity'
import { formatDateShort } from '../utils/dateUtils'
import { translateBackendKey } from '../utils/translateBackendKey'
import DataTable, { Column, ActionButton } from '../components/DataTable'

interface UserType {
  id: number
  username: string
  email: string
  is_active: boolean
  is_admin: boolean
  created_at: string
  last_login: string | null
}

const Settings: React.FC = () => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { mode, setTheme } = useTheme()
  const queryClient = useQueryClient()
  const { tab } = useParams<{ tab?: string }>()
  const { data: systemSettingsData } = useQuery({
    queryKey: ['systemSettings'],
    queryFn: async () => {
      const response = await settingsAPI.getSystemSettings()
      return response.data
    },
  })
  const systemSettings = systemSettingsData?.settings
  const mqttBetaEnabled = systemSettings?.mqtt_beta_enabled ?? false

  // Get tab order based on user role
  const getTabOrder = React.useCallback(() => {
    const baseTabs = ['account', 'appearance', 'preferences', 'notifications']
    if (user?.is_admin) {
      return [
        ...baseTabs,
        'system',
        ...(mqttBetaEnabled ? ['mqtt'] : []),
        'beta',
        'cache',
        'logs',
        'mounts',
        'packages',
        'scripts',
        'export',
        'users',
        'activity',
      ]
    }
    return [...baseTabs, 'mounts', 'scripts', 'export', 'activity']
  }, [user?.is_admin, mqttBetaEnabled])

  const tabOrder = React.useMemo(() => getTabOrder(), [getTabOrder])

  // Determine active tab from URL or default to 'account'
  const getTabIndexFromPath = React.useCallback(
    (tabPath?: string): number => {
      if (!tabPath) return 0
      const index = tabOrder.indexOf(tabPath)
      return index >= 0 ? index : 0
    },
    [tabOrder]
  )

  const [activeTab, setActiveTab] = useState(() => getTabIndexFromPath(tab))

  const currentTabId = React.useMemo(() => {
    return tabOrder[activeTab] ?? tabOrder[0]
  }, [activeTab, tabOrder])

  // Update active tab when URL changes
  useEffect(() => {
    setActiveTab(getTabIndexFromPath(tab))
  }, [tab, getTabIndexFromPath])
  const [showCreateUser, setShowCreateUser] = useState(false)
  const [editingUser, setEditingUser] = useState<UserType | null>(null)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<UserType | null>(null)
  const [changePasswordForm, setChangePasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  })

  // Change password mutation (for current user)
  const changePasswordMutation = useMutation({
    mutationFn: (passwordData: { current_password: string; new_password: string }) =>
      settingsAPI.changePassword(passwordData),
    onSuccess: () => {
      toast.success(t('settings.toasts.passwordChanged'))
      setChangePasswordForm({ current_password: '', new_password: '', confirm_password: '' })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(
        translateBackendKey(error.response?.data?.detail) ||
          t('settings.toasts.failedToChangePassword')
      )
    },
  })

  // Users
  const { data: usersData, isLoading: loadingUsers } = useQuery({
    queryKey: ['users'],
    queryFn: settingsAPI.getUsers,
    enabled: user?.is_admin === true,
  })

  const createUserMutation = useMutation({
    mutationFn: settingsAPI.createUser,
    onSuccess: () => {
      toast.success(t('settings.toasts.userCreated'))
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowCreateUser(false)
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(
        translateBackendKey(error.response?.data?.detail) || t('settings.toasts.failedToCreateUser')
      )
    },
  })

  const updateUserMutation = useMutation({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    mutationFn: ({ userId, userData }: { userId: number; userData: any }) =>
      settingsAPI.updateUser(userId, userData),
    onSuccess: () => {
      toast.success(t('settings.toasts.userUpdated'))
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setEditingUser(null)
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(
        translateBackendKey(error.response?.data?.detail) || t('settings.toasts.failedToUpdateUser')
      )
    },
  })

  const deleteUserMutation = useMutation({
    mutationFn: settingsAPI.deleteUser,
    onSuccess: () => {
      toast.success(t('settings.toasts.userDeleted'))
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setDeleteConfirmUser(null)
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(
        translateBackendKey(error.response?.data?.detail) || t('settings.toasts.failedToDeleteUser')
      )
    },
  })

  const resetPasswordMutation = useMutation({
    mutationFn: ({ userId, newPassword }: { userId: number; newPassword: string }) =>
      settingsAPI.resetUserPassword(userId, newPassword),
    onSuccess: () => {
      toast.success(t('settings.toasts.passwordReset'))
      setShowPasswordModal(false)
      setSelectedUserId(null)
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      toast.error(
        translateBackendKey(error.response?.data?.detail) ||
          t('settings.toasts.failedToResetPassword')
      )
    },
  })

  // Form states
  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    password: '',
    is_admin: false,
  })
  const [passwordForm, setPasswordForm] = useState({
    new_password: '',
  })

  const handleCreateUser = (e: React.FormEvent) => {
    e.preventDefault()
    createUserMutation.mutate(userForm)
  }

  const handleUpdateUser = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingUser) {
      updateUserMutation.mutate({
        userId: editingUser.id,
        userData: userForm,
      })
    }
  }

  const handleResetPassword = (e: React.FormEvent) => {
    e.preventDefault()
    if (selectedUserId) {
      resetPasswordMutation.mutate({
        userId: selectedUserId,
        newPassword: passwordForm.new_password,
      })
    }
  }

  const handleDeleteUser = () => {
    if (deleteConfirmUser) {
      deleteUserMutation.mutate(deleteConfirmUser.id)
    }
  }

  const openPasswordModal = (userId: number) => {
    setSelectedUserId(userId)
    setShowPasswordModal(true)
    setPasswordForm({ new_password: '' })
  }

  const openEditUser = (user: UserType) => {
    setEditingUser(user)
    setUserForm({
      username: user.username,
      email: user.email,
      password: '',
      is_admin: user.is_admin,
    })
  }

  const openCreateUser = () => {
    setShowCreateUser(true)
    setUserForm({
      username: '',
      email: '',
      password: '',
      is_admin: false,
    })
  }

  // Column definitions for Users table
  const userColumns: Column<UserType>[] = [
    {
      id: 'user',
      label: t('settings.users.table.user'),
      render: (user) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {user.username}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user.email}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'status',
      label: t('settings.users.table.status'),
      render: (user) => (
        <Chip
          label={
            user.is_active ? t('settings.users.status.active') : t('settings.users.status.inactive')
          }
          color={user.is_active ? 'success' : 'error'}
          size="small"
        />
      ),
    },
    {
      id: 'role',
      label: t('settings.users.table.role'),
      render: (user) => (
        <Chip
          label={user.is_admin ? t('settings.users.roles.admin') : t('settings.users.roles.user')}
          color={user.is_admin ? 'secondary' : 'default'}
          size="small"
        />
      ),
    },
    {
      id: 'created_at',
      label: t('settings.users.table.created'),
      render: (user) => <Typography variant="body2">{formatDateShort(user.created_at)}</Typography>,
    },
    {
      id: 'last_login',
      label: t('settings.users.table.lastLogin'),
      render: (user) => (
        <Typography variant="body2" color="text.secondary">
          {formatDateShort(user.last_login)}
        </Typography>
      ),
    },
  ]

  // Action buttons for Users table
  const userActions: ActionButton<UserType>[] = [
    {
      icon: <Edit size={16} />,
      label: t('settings.users.actions.edit'),
      onClick: openEditUser,
      color: 'primary',
      tooltip: t('settings.users.actions.edit'),
    },
    {
      icon: <Key size={16} />,
      label: t('settings.users.actions.resetPassword'),
      onClick: (user) => openPasswordModal(user.id),
      color: 'warning',
      tooltip: t('settings.users.actions.resetPassword'),
    },
    {
      icon: <Trash2 size={16} />,
      label: t('settings.users.actions.delete'),
      onClick: setDeleteConfirmUser,
      color: 'error',
      tooltip: t('settings.users.actions.delete'),
    },
  ]

  return (
    <Box>
      {/* Content is controlled by sidebar navigation */}

      {/* Profile Tab */}
      {currentTabId === 'account' && (
        <Box>
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t('settings.password.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('settings.password.subtitle')}
            </Typography>
          </Box>
          <Card sx={{ maxWidth: 600 }}>
            <Box sx={{ p: 3 }}>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  if (changePasswordForm.new_password !== changePasswordForm.confirm_password) {
                    toast.error(t('settings.toasts.passwordsDoNotMatch'))
                    return
                  }
                  changePasswordMutation.mutate({
                    current_password: changePasswordForm.current_password,
                    new_password: changePasswordForm.new_password,
                  })
                }}
              >
                <Stack spacing={3}>
                  <TextField
                    label={t('settings.password.current')}
                    type="password"
                    value={changePasswordForm.current_password}
                    onChange={(e) =>
                      setChangePasswordForm({
                        ...changePasswordForm,
                        current_password: e.target.value,
                      })
                    }
                    required
                    fullWidth
                  />

                  <TextField
                    label={t('settings.password.new')}
                    type="password"
                    value={changePasswordForm.new_password}
                    onChange={(e) =>
                      setChangePasswordForm({ ...changePasswordForm, new_password: e.target.value })
                    }
                    required
                    fullWidth
                  />

                  <TextField
                    label={t('settings.password.confirm')}
                    type="password"
                    value={changePasswordForm.confirm_password}
                    onChange={(e) =>
                      setChangePasswordForm({
                        ...changePasswordForm,
                        confirm_password: e.target.value,
                      })
                    }
                    required
                    fullWidth
                    error={
                      changePasswordForm.confirm_password !== '' &&
                      changePasswordForm.new_password !== changePasswordForm.confirm_password
                    }
                    helperText={
                      changePasswordForm.confirm_password !== '' &&
                      changePasswordForm.new_password !== changePasswordForm.confirm_password
                        ? t('settings.password.noMatch')
                        : ''
                    }
                  />

                  <Box>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={changePasswordMutation.isPending}
                      startIcon={
                        changePasswordMutation.isPending ? <CircularProgress size={16} /> : null
                      }
                    >
                      {changePasswordMutation.isPending
                        ? t('settings.password.submitting')
                        : t('settings.password.submit')}
                    </Button>
                  </Box>
                </Stack>
              </form>
            </Box>
          </Card>
        </Box>
      )}

      {/* Appearance Tab */}
      {currentTabId === 'appearance' && (
        <Box>
          <Box>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              {t('settings.appearance.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('settings.appearance.subtitle')}
            </Typography>
          </Box>
          <Card sx={{ maxWidth: 600 }}>
            <Box sx={{ p: 3 }}>
              <Stack spacing={3}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {mode === 'dark' ? <Moon size={24} /> : <Sun size={24} />}
                    <Box>
                      <Typography variant="subtitle1" fontWeight={500}>
                        {t('settings.appearance.theme')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {t('settings.appearance.chooseTheme')}
                      </Typography>
                    </Box>
                  </Box>
                  <FormControl sx={{ minWidth: 120 }} size="small">
                    <Select
                      value={mode}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      onChange={(e) => setTheme(e.target.value as any)}
                      displayEmpty
                      inputProps={{ 'aria-label': t('settings.appearance.themeAriaLabel') }}
                    >
                      {availableThemes.map((themeOption) => (
                        <MenuItem key={themeOption.id} value={themeOption.id}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {themeOption.icon === 'Sun' ? <Sun size={16} /> : <Moon size={16} />}
                            {themeOption.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Stack>
            </Box>
          </Card>
        </Box>
      )}

      {/* Preferences Tab */}
      {currentTabId === 'preferences' && <PreferencesTab />}

      {/* Notifications Tab */}
      {currentTabId === 'notifications' && <NotificationsTab />}

      {/* System Settings Tab - Admin Only */}
      {currentTabId === 'system' && user?.is_admin && <SystemSettingsTab />}

      {/* MQTT Settings Tab - Admin Only */}
      {currentTabId === 'mqtt' && user?.is_admin && mqttBetaEnabled && <MqttSettingsTab />}

      {/* Beta Features Tab - Admin Only */}
      {currentTabId === 'beta' && user?.is_admin && <BetaFeaturesTab />}

      {/* Cache Management Tab - Admin Only */}
      {currentTabId === 'cache' && user?.is_admin && <CacheManagementTab />}

      {/* Log Management Tab - Admin Only */}
      {currentTabId === 'logs' && user?.is_admin && <LogManagementTab />}

      {/* Mounts Management Tab */}
      {currentTabId === 'mounts' && <MountsManagementTab />}

      {/* System Packages Tab - Admin Only */}
      {currentTabId === 'packages' && user?.is_admin && <PackagesTab />}

      {/* Scripts Tab */}
      {currentTabId === 'scripts' && <Scripts />}

      {/* Export/Import Tab */}
      {currentTabId === 'export' && <ExportImportTab />}

      {/* User Management Tab - Admin Only */}
      {currentTabId === 'users' && user?.is_admin && (
        <Box>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}
          >
            <Typography variant="h6" fontWeight={600}>
              {t('settings.users.title')}
            </Typography>
            <Button variant="contained" startIcon={<Plus size={18} />} onClick={openCreateUser}>
              {t('settings.users.addUser')}
            </Button>
          </Box>

          <DataTable
            data={usersData?.data?.users || []}
            columns={userColumns}
            actions={userActions}
            getRowKey={(user) => user.id}
            loading={loadingUsers}
            emptyState={{
              icon: <Users size={48} />,
              title: t('settings.users.emptyState.title'),
              description: t('settings.users.emptyState.description'),
            }}
            variant="outlined"
          />
        </Box>
      )}

      {/* Activity Tab */}
      {currentTabId === 'activity' && <Activity />}

      {/* Create/Edit User Modal */}
      <Dialog
        open={showCreateUser || !!editingUser}
        onClose={() => {
          setShowCreateUser(false)
          setEditingUser(null)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingUser
            ? t('settings.users.editDialog.title')
            : t('settings.users.createDialog.title')}
        </DialogTitle>
        <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
          <DialogContent>
            <Stack spacing={3}>
              <TextField
                label={t('settings.users.fields.username')}
                value={userForm.username}
                onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                required
                fullWidth
              />

              <TextField
                label={t('settings.users.fields.email')}
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                required
                fullWidth
              />

              {!editingUser && (
                <TextField
                  label={t('settings.users.fields.password')}
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  required
                  fullWidth
                />
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={userForm.is_admin}
                    onChange={(e) => setUserForm({ ...userForm, is_admin: e.target.checked })}
                  />
                }
                label={t('settings.users.fields.isAdmin')}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowCreateUser(false)
                setEditingUser(null)
              }}
            >
              {t('settings.users.buttons.cancel')}
            </Button>
            <Button type="submit" variant="contained">
              {editingUser
                ? t('settings.users.buttons.update')
                : t('settings.users.buttons.create')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Password Reset Modal */}
      <Dialog
        open={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false)
          setSelectedUserId(null)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('settings.users.resetPasswordDialog.title')}</DialogTitle>
        <form onSubmit={handleResetPassword}>
          <DialogContent>
            <TextField
              label={t('settings.password.new')}
              type="password"
              value={passwordForm.new_password}
              onChange={(e) => setPasswordForm({ new_password: e.target.value })}
              required
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowPasswordModal(false)
                setSelectedUserId(null)
              }}
            >
              {t('settings.users.buttons.cancel')}
            </Button>
            <Button type="submit" variant="contained">
              {t('settings.users.actions.resetPassword')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog
        open={!!deleteConfirmUser}
        onClose={() => setDeleteConfirmUser(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                backgroundColor: 'error.lighter',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AlertCircle size={24} color="#d32f2f" />
            </Box>
            <Typography variant="h6" fontWeight={600}>
              {t('settings.users.deleteDialog.title')}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t('settings.users.deleteDialog.message', { username: deleteConfirmUser?.username })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('settings.users.deleteDialog.warning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmUser(null)}>
            {t('settings.users.buttons.cancel')}
          </Button>
          <Button
            onClick={handleDeleteUser}
            variant="contained"
            color="error"
            disabled={deleteUserMutation.isPending}
            startIcon={deleteUserMutation.isPending ? <CircularProgress size={16} /> : null}
          >
            {deleteUserMutation.isPending
              ? t('settings.users.deleteDialog.deleting')
              : t('settings.users.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default Settings
